import React, { useState, useMemo, useCallback, useEffect } from 'react';
import { Calendar, momentLocalizer, Views } from 'react-big-calendar';
import moment from 'moment';
import 'moment/locale/pt-br'; // Importar localização em português
import { format } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import 'react-big-calendar/lib/css/react-big-calendar.css';
import { DateInput } from './ui';
import { doc, updateDoc } from 'firebase/firestore';
import { db } from '../firebase';
import TimelineToolbar from './booking/timeline/TimelineToolbar'; // Importar o componente de barra de ferramentas centralizado

// Configurar o moment para usar o locale português
moment.locale('pt-br');

// Usar o momentLocalizer para React Big Calendar
const localizer = momentLocalizer(moment);

// Definir as visualizações disponíveis - deve corresponder às visualizações do React Big Calendar
const CUSTOM_VIEWS = {
  day: true,
  week: true,
  month: true,
  agenda: true,
  // Visualizações com recursos
  ...(Views.RESOURCE_DAY ? { resourceDay: true } : {}),
  ...(Views.RESOURCE_WEEK ? { resourceWeek: true } : {})
};

// Função para gerar cores distintas para profissionais
const generateProfessionalColor = (index) => {
  // Array de cores distintas para profissionais
  const professionalColors = [
    '#3B82F6', // Azul
    '#F59E0B', // Âmbar
    '#10B981', // Verde
    '#8B5CF6', // Violeta
    '#EC4899', // Rosa
    '#06B6D4', // Ciano
    '#F97316', // Laranja
    '#14B8A6', // Turquesa
    '#6366F1', // Índigo
    '#D946EF', // Fúcsia
    '#0EA5E9', // Azul claro
    '#84CC16'  // Verde limão
  ];
  
  // Usar index modulo o tamanho do array para sempre ter uma cor válida
  return professionalColors[index % professionalColors.length];
};

/**
 * Componente de visualização de agenda usando React Big Calendar
 * Exibe agendamentos em formato de calendário responsivo
 * Suporta visualização por dia, semana e profissionais
 */
const AppointmentTimeline = ({
  appointments = [],
  selectedDate,
  professionals,
  salonConfig,
  onEventUpdate,
  onTimeBlocked,
  onViewChange,
  onSelectEvent
}) => {
  // Estado para controlar a data atual
  const [date, setDate] = useState(() => {
    if (selectedDate) {
      // Garantir que a data seja interpretada no fuso horário local
      const [year, month, day] = selectedDate.split('-').map(Number);
      return new Date(year, month - 1, day);
    }
    return new Date();
  });
  // Novo estado para controlar o número de colunas visíveis
  const [visibleColumns, setVisibleColumns] = useState(5); // Limite inicial de colunas
  // Novo estado para controlar o modal de bloqueio de horário
  const [blockModalOpen, setBlockModalOpen] = useState(false);
  const [blockingData, setBlockingData] = useState({
    date: new Date(),
    startTime: '09:00',
    endTime: '10:00',
    reason: '',
    professionalId: 'all'
  });
  
  // Estado para controlar a visualização atual do calendário
  // Garantir que começamos com uma visualização suportada
  const [currentView, setCurrentView] = useState(() => {
    // Verificar se 'day' está disponível, caso contrário usar a primeira visualização disponível
    if (CUSTOM_VIEWS.day) return 'day';
    return Object.keys(CUSTOM_VIEWS)[0] || 'day';
  });
  
  // Logar os agendamentos recebidos pelo componente
  useEffect(() => {
    console.log("AppointmentTimeline recebeu agendamentos:", appointments);
    console.log("Data selecionada:", selectedDate);
  }, [appointments, selectedDate]);
  
  // Função segura para mudar a visualização
  const changeView = useCallback((newView) => {
    if (CUSTOM_VIEWS[newView]) {
      setCurrentView(newView);
    } else {
      console.warn(`Visualização "${newView}" não está disponível, usando fallback.`);
      // Voltar para 'day' ou a primeira visualização disponível
      setCurrentView(CUSTOM_VIEWS.day ? 'day' : Object.keys(CUSTOM_VIEWS)[0]);
    }
  }, []);
  
  // Definir os rótulos para as visualizações do calendário
  const viewLabels = {
    day: 'Dia',
    week: 'Semana',
    month: 'Mês',
    agenda: 'Agenda',
    resourceDay: 'Por Profissional (Dia)',
    resourceWeek: 'Por Profissional (Semana)'
  };
  
  // Preparar dados dos profissionais como recursos para o calendário
  const resources = useMemo(() => {
    if (!professionals || professionals.length === 0) {
      return [{ id: 'default', title: 'Sem profissionais' }];
    }
    
    return professionals.map((professional, index) => ({
      id: professional.id,
      title: professional.nome,
      color: generateProfessionalColor(index),
      avatarUrl: professional.avatarUrl
    }));
  }, [professionals]);
  
  // Adicionar um recurso "Qualquer Profissional" para agendamentos sem profissional específico
  const resourcesWithAny = useMemo(() => {
    return [
      ...resources,
      { 
        id: 'any', 
        title: 'Qualquer Profissional',
        color: '#6B7280' // Cinza
      }
    ];
  }, [resources]);

  // Converter agendamentos para o formato esperado pelo Big Calendar
  const events = useMemo(() => {
    console.log("Processando agendamentos:", appointments.length);
    
    if (!Array.isArray(appointments) || appointments.length === 0) {
      console.log("Nenhum agendamento para processar");
      return [];
    }
    
    return appointments.map(appointment => {
      try {
        // Extrair data e hora
        let appointmentDate;
        
        // Verificar o tipo da data e converter adequadamente
        if (appointment.data instanceof Date) {
          appointmentDate = appointment.data;
        } else if (typeof appointment.data === 'object' && appointment.data.toDate) {
          // Timestamp do Firestore
          appointmentDate = appointment.data.toDate();
        } else if (typeof appointment.data === 'string') {
          // Corrigir problema de fuso horário adicionando T00:00:00 para garantir que a data seja interpretada no dia correto
          appointmentDate = new Date(`${appointment.data.split('T')[0]}T00:00:00`);
        } else {
          console.log('Data inválida para o agendamento:', appointment);
          appointmentDate = new Date(); // Fallback para data atual
        }
        
        // Extrair hora e minuto do horário (formato: "HH:MM")
        const [startHours, startMinutes] = (appointment.horario || '00:00').split(':').map(Number);
        
        // Criar objeto de data/hora de início
        const startDateTime = new Date(appointmentDate);
        startDateTime.setHours(startHours, startMinutes, 0);
        
        // Verificar se a data é válida
        if (isNaN(startDateTime.getTime())) {
          console.log('Data de início inválida para:', appointment);
          return null;
        }
        
        // Calcular horário de término
        const duration = appointment.duracaoEfetiva || appointment.duracaoTotal || 30;
        const endDateTime = new Date(startDateTime.getTime() + duration * 60 * 1000);
        
        // Verificar se a data de término é válida
        if (isNaN(endDateTime.getTime())) {
          console.log('Data de término inválida para:', appointment);
          return null;
        }
        
        // Determinar o recurso (profissional)
        let resourceId = 'any';
        
        // Verificar se o profissional é um objeto ou um ID
        if (appointment.profissional) {
          console.log("Profissional encontrado:", typeof appointment.profissional, appointment.profissional);
          
          if (typeof appointment.profissional === 'string') {
            resourceId = appointment.profissional;
          } else if (typeof appointment.profissional === 'object' && appointment.profissional !== null) {
            // O profissional é um objeto completo, extrair o ID
            resourceId = appointment.profissional.id || 'any';
            console.log("ID do profissional extraído:", resourceId);
          }
        }
        
        // Determinar a cor com base no tipo de serviço
        let serviceColor = '#4F46E5'; // Cor padrão (indigo)
        let serviceName = 'Serviço';
        let hasSetupTime = false;
        let hasFinishTime = false;
        
        // Verificar se o agendamento tem serviços
        if (appointment.servicos && appointment.servicos.length > 0) {
          console.log("Serviços encontrados:", typeof appointment.servicos[0], appointment.servicos[0]);
          
          const firstService = appointment.servicos[0];
          
          // Verificar se o serviço é um objeto ou um ID de referência
          if (typeof firstService === 'object' && firstService !== null) {
            // O serviço já é um objeto com dados
            serviceName = firstService.nome || 'Serviço';
            hasSetupTime = firstService.setupTime > 0;
            hasFinishTime = firstService.finishTime > 0;
            
            // Determinar cor com base no nome do serviço
            const serviceType = serviceName.toLowerCase();
            if (serviceType.includes('corte')) serviceColor = '#3B82F6'; // azul
            else if (serviceType.includes('color')) serviceColor = '#EC4899'; // rosa
            else if (serviceType.includes('manicure') || serviceType.includes('pedicure')) serviceColor = '#06B6D4'; // ciano
            else if (serviceType.includes('hidrat')) serviceColor = '#10B981'; // verde
            else serviceColor = '#8B5CF6'; // violeta
          }
        }
        
        const eventData = {
          id: appointment.id,
          title: appointment.cliente,
          start: startDateTime,
          end: endDateTime,
          resourceId: resourceId,
          serviceColor,
          serviceName,
          appointment, // Dados completos do agendamento
          hasSetupTime,
          hasFinishTime,
          status: appointment.cancelado ? 'canceled' : appointment.confirmado ? 'confirmed' : 'pending'
        };
        
        return eventData;
      } catch (error) {
        console.error("Erro ao processar agendamento:", error, appointment);
        return null;
      }
    }).filter(event => {
      // Filtrar eventos inválidos (com datas indefinidas)
      return event !== null && 
             event.start instanceof Date && !isNaN(event.start.getTime()) && 
             event.end instanceof Date && !isNaN(event.end.getTime());
    });
  }, [appointments]);
  
  // Atualizar a data quando selectedDate mudar
  React.useEffect(() => {
    if (selectedDate) {
      // Garantir que a data seja interpretada no fuso horário local
      const [year, month, day] = selectedDate.split('-').map(Number);
      setDate(new Date(year, month - 1, day));
    }
  }, [selectedDate]);
  
  // Garantir que o calendário inicie no dia atual
  React.useEffect(() => {
    // Na primeira renderização, navegar para o dia atual
    const today = new Date();
    const formattedDate = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;
    
    // Se a data selecionada for diferente da data atual, atualizar para hoje
    if (selectedDate !== formattedDate) {
      // Chamamos diretamente a navegação para hoje
      setDate(today);
    }
  }, []); // Executar apenas uma vez na montagem
  
  // Personalizar o estilo dos eventos
  const eventStyleGetter = (event) => {
    // Encontrar o recurso correspondente para obter a cor do profissional
    const resource = resourcesWithAny.find(r => r.id === event.resourceId);
    
    if (!resource) {
      console.warn("Recurso não encontrado para o evento:", event.resourceId, event);
    }
    
    const backgroundColor = resource?.color || '#4F46E5';
    
    // Estilo base
    const style = {
      backgroundColor: backgroundColor,
      borderRadius: '4px',
      opacity: 0.9,
      color: 'white',
      border: '0px',
      display: 'block',
      position: 'relative'
    };
    
    // Adicionar estilo baseado no status
    if (event.status === 'canceled') {
      style.backgroundColor = '#EF4444'; // Vermelho para cancelados
      style.textDecoration = 'line-through';
      style.opacity = 0.7;
    } else if (event.status === 'pending') {
      style.backgroundColor = '#F59E0B'; // Âmbar para pendentes
    }
    
    // Determinar classe adicional
    let className = 'rbc-event-custom';
    if (event.hasSetupTime || event.hasFinishTime) {
      className += ' has-additional-time';
    }
    
    // Adicionar classe especial para agendamentos feitos pelo atendente
    if (event.appointment.source === 'atendente' || event.appointment.deviceType === 'atendente') {
      className += ' attendant-booked';
      style.borderLeft = '3px solid #ffffff'; // Borda branca à esquerda para destacar
    }
    
    return { style, className };
  };
  
  // Componente personalizado para exibição de eventos
  const EventComponent = ({ event }) => {
    const { appointment } = event;
    const timeString = `${format(event.start, 'HH:mm')} - ${format(event.end, 'HH:mm')}`;
    
    // Determinar o ícone com base na origem do agendamento
    // Vamos usar a propriedade 'source' ou 'deviceType' se existir, ou um valor padrão
    let sourceIcon;
    if (appointment.source === 'atendente' || appointment.deviceType === 'atendente') {
      // Ícone para agendamentos feitos pelo atendente
      sourceIcon = (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z" />
        </svg>
      );
    } else if (appointment.source === 'mobile' || appointment.deviceType === 'mobile') {
      // Ícone para agendamentos feitos via dispositivo móvel
      sourceIcon = (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 18h.01M8 21h8a2 2 0 002-2V5a2 2 0 00-2-2H8a2 2 0 00-2 2v14a2 2 0 002 2z" />
        </svg>
      );
    } else {
      // Ícone para agendamentos feitos via desktop (padrão)
      sourceIcon = (
        <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-0.5" fill="none" viewBox="0 0 24 24" stroke="currentColor">
          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.75 17L9 20l-1 1h8l-1-1-.75-3M3 13h18M5 17h14a2 2 0 002-2V5a2 2 0 00-2-2H5a2 2 0 00-2 2v10a2 2 0 002 2z" />
        </svg>
      );
    }
    
    return (
      <div className="p-1 h-full overflow-hidden">
        {/* Primeira linha: Nome do cliente e horário */}
        <div className="font-semibold text-xs flex items-center justify-between">
          <div className="flex items-center truncate">
            {sourceIcon}
            <span className="truncate">{appointment.cliente}</span>
          </div>
          <span className="text-2xs whitespace-nowrap ml-1">{timeString}</span>
        </div>
        
        {/* Segunda linha: Detalhes do serviço e preço, só mostra se tiver espaço */}
        {appointment.servicoNome && (
          <div className="text-2xs truncate mt-0.5">
            {appointment.servicoNome}
            {appointment.preco && 
              <span className="ml-1">
                {`R$${appointment.preco.toFixed(2)}`}
              </span>
            }
          </div>
        )}
        
        {/* Tag de bloqueio, se for o caso */}
        {appointment.tipo === 'bloqueio' && (
          <div className="text-2xs bg-red-100 text-red-800 px-1 rounded mt-0.5">
            Bloqueado
          </div>
        )}
      </div>
    );
  };

  // Componente personalizado para cabeçalho de recursos (profissionais)
  const ResourceHeader = ({ resource }) => (
    <div 
      className="text-center flex flex-col items-center justify-center"
      style={{ 
        backgroundColor: resource.color || '#d1d5db', // Cor padrão cinza se não houver cor definida 
        color: 'white',
        height: 'auto', // Permitir que a altura seja apenas a necessária
        padding: '2px 1px'
      }}
    >
      {resource.avatarUrl ? (
        <img 
          src={resource.avatarUrl} 
          alt={resource.title}
          className="w-6 h-6 rounded-full border-2 border-white object-cover" 
        />
      ) : (
        <div className="w-6 h-6 rounded-full bg-gray-300 flex items-center justify-center border-2 border-white">
          <span className="text-sm font-semibold text-gray-700">
            {resource.title.charAt(0)}
          </span>
        </div>
      )}
      <span className="font-semibold text-xs truncate w-full" style={{maxWidth: "100%"}}>
        {resource.title}
      </span>
    </div>
  );
  
  // Função para ajustar o número de colunas visíveis
  const handleColumnAdjustment = (columnCount) => {
    setVisibleColumns(columnCount);
  };
  
  // Função para abrir o modal de bloqueio de horário
  const openBlockingModal = () => {
    setBlockingData({
      ...blockingData,
      date: date
    });
    setBlockModalOpen(true);
  };
  
  // Função para aplicar o bloqueio de horário
  const applyTimeBlocking = () => {
    // Aqui você pode implementar a lógica para salvar o bloqueio no banco de dados
    console.log('Bloquear horário:', blockingData);
    
    // Criar um "appointment" de bloqueio que seria adicionado à lista
    const blockingEvent = {
      id: `block-${Date.now()}`,
      data: blockingData.date,
      horario: blockingData.startTime,
      duracaoTotal: calculateDuration(blockingData.startTime, blockingData.endTime),
      cliente: 'BLOQUEADO',
      descricao: blockingData.reason,
      profissional: blockingData.professionalId === 'all' ? null : blockingData.professionalId,
      tipo: 'bloqueio'
    };
    
    // Aqui você enviaria este objeto para o backend (implementar depois)
    // Por exemplo: addBlockedTime(salonId, blockingEvent);
    
    // Por enquanto, vamos apenas fechar o modal
    setBlockModalOpen(false);
  };
  
  // Função para calcular a duração em minutos entre dois horários
  const calculateDuration = (startTime, endTime) => {
    if (!startTime || !endTime) return 0;
    
    const start = startTime.split(':');
    const end = endTime.split(':');
    
    const startMinutes = parseInt(start[0]) * 60 + parseInt(start[1]);
    const endMinutes = parseInt(end[0]) * 60 + parseInt(end[1]);
    
    return endMinutes - startMinutes;
  };
  
  // Função para personalizar a barra de ferramentas
  function CustomToolbar({ date, onNavigate, onView }) {
    // Formatar a data atual
    const formattedDate = React.useMemo(() => {
      return format(date, 'd MMM yyyy', { locale: ptBR });
    }, [date]);
    
    // Estado para controlar o menu de opções avançadas
    const [showAdvancedMenu, setShowAdvancedMenu] = useState(false);
    
    // Referência para o menu dropdown
    const advancedMenuRef = React.useRef(null);
    
    // Função para mudar a visualização com segurança
    const handleViewChange = (newView) => {
      // Usar a função changeView do componente pai
      if (CUSTOM_VIEWS[newView]) {
        onView(newView);
      } else {
        console.warn(`Visualização "${newView}" não disponível no CustomToolbar`);
        // Tentar uma visualização segura
        onView('day');
      }
      // Fechar o menu se estiver aberto
      setShowAdvancedMenu(false);
    };
    
    // Fechar o menu quando clicar fora dele
    React.useEffect(() => {
      function handleClickOutside(event) {
        if (advancedMenuRef.current && !advancedMenuRef.current.contains(event.target)) {
          setShowAdvancedMenu(false);
        }
      }
      
      document.addEventListener("mousedown", handleClickOutside);
      return () => {
        document.removeEventListener("mousedown", handleClickOutside);
      };
    }, []);
    
    return (
      <div className="flex flex-wrap items-center justify-between p-1 mb-2 border-b">
        {/* Grupo de navegação - layout mais compacto */}
        <div className="flex items-center space-x-1 mb-1 sm:mb-0">
          <button 
            type="button" 
            onClick={() => onNavigate('TODAY')}
            className="px-2 py-1 bg-blue-50 hover:bg-blue-100 text-blue-600 font-medium rounded-md text-xs sm:text-sm transition-colors"
          >
            Hoje
          </button>
          
          <div className="flex border rounded-md overflow-hidden">
            <button 
              type="button" 
              onClick={() => onNavigate('PREV')}
              className="px-2 py-1 bg-white hover:bg-gray-100 text-gray-700 border-r"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-4 sm:w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M12.707 5.293a1 1 0 010 1.414L9.414 10l3.293 3.293a1 1 0 01-1.414 1.414l-4-4a1 1 0 010-1.414l4-4a1 1 0 011.414 0z" clipRule="evenodd" />
              </svg>
            </button>
            <button 
              type="button" 
              onClick={() => onNavigate('NEXT')}
              className="px-2 py-1 bg-white hover:bg-gray-100 text-gray-700"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 sm:h-4 sm:w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M7.293 14.707a1 1 0 010-1.414L10.586 10 7.293 6.707a1 1 0 011.414-1.414l4 4a1 1 0 010 1.414l-4 4a1 1 0 01-1.414 0z" clipRule="evenodd" />
              </svg>
            </button>
          </div>
          
          <h2 className="text-sm sm:text-base font-semibold text-gray-800 mx-1">
            {formattedDate}
          </h2>
        </div>
        
        {/* Visualizações principais - botões menores */}
        <div className="flex">
          <div className="flex border rounded-md overflow-hidden">
            <button 
              type="button" 
              onClick={() => handleViewChange('day')}
              className={`px-2 py-1 text-xs ${currentView === 'day' ? 'bg-blue-50 text-blue-600 font-medium' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
            >
              Dia
            </button>
            <button 
              type="button" 
              onClick={() => handleViewChange('week')}
              className={`px-2 py-1 text-xs ${currentView === 'week' ? 'bg-blue-50 text-blue-600 font-medium' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
            >
              Sem
            </button>
            <button 
              type="button" 
              onClick={() => handleViewChange('month')}
              className={`px-2 py-1 text-xs ${currentView === 'month' ? 'bg-blue-50 text-blue-600 font-medium' : 'bg-white text-gray-700 hover:bg-gray-100'}`}
            >
              Mês
            </button>
          </div>
          
          {/* Botão de opções avançadas - mais compacto */}
          <div className="relative ml-1" ref={advancedMenuRef}>
            <button 
              type="button" 
              onClick={() => setShowAdvancedMenu(!showAdvancedMenu)}
              className="px-2 py-1 bg-white hover:bg-gray-100 text-gray-700 border rounded-md flex items-center"
            >
              <svg xmlns="http://www.w3.org/2000/svg" className="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
                <path fillRule="evenodd" d="M11.49 3.17c-.38-1.56-2.6-1.56-2.98 0a1.532 1.532 0 01-2.286.948c-1.372-.836-2.942.734-2.106 2.106.54.886.061 2.042-.947 2.287-1.561.379-1.561 2.6 0 2.978a1.532 1.532 0 01.947 2.287c-.836 1.372.734 2.942 2.106 2.106a1.532 1.532 0 012.287.947c.379 1.561 2.6 1.561 2.978 0a1.533 1.533 0 012.287-.947c1.372.836 2.942-.734 2.106-2.106a1.533 1.533 0 01.947-2.287c1.561-.379 1.561-2.6 0-2.978a1.532 1.532 0 01-.947-2.287c.836-1.372-.734-2.942-2.106-2.106a1.532 1.532 0 01-2.287-.947zM10 13a3 3 0 100-6 3 3 0 000 6z" clipRule="evenodd" />
              </svg>
            </button>
            
            {/* Menu dropdown de opções avançadas */}
            {showAdvancedMenu && (
              <div className="absolute right-0 mt-1 py-1 w-48 bg-white rounded-md shadow-lg z-20 border text-xs">
                {/* Opção de bloquear horário */}
                <button 
                  type="button" 
                  className="w-full px-3 py-1 text-left hover:bg-gray-100 flex items-center"
                  onClick={() => {
                    openBlockingModal();
                    setShowAdvancedMenu(false);
                  }}
                >
                  <svg xmlns="http://www.w3.org/2000/svg" className="h-3 w-3 mr-1" viewBox="0 0 20 20" fill="currentColor">
                    <path fillRule="evenodd" d="M5 9V7a5 5 0 0110 0v2a2 2 0 012 2v5a2 2 0 01-2 2H5a2 2 0 01-2-2v-5a2 2 0 012-2zm8-2v2H7V7a3 3 0 016 0z" clipRule="evenodd" />
                  </svg>
                  Bloquear Horário
                </button>
                
                {/* Opção de ajustar número de colunas */}
                <div className="px-3 py-1 border-t">
                  <p className="text-xxs text-gray-500 mb-1">Colunas visíveis:</p>
                  <div className="flex flex-wrap gap-1">
                    {[3, 5, 8, 10, 12].map(count => (
                      <button 
                        key={count}
                        className={`w-6 h-6 rounded flex items-center justify-center text-xxs 
                                  ${visibleColumns === count ? 'bg-blue-500 text-white' : 'bg-gray-200 hover:bg-gray-300 text-gray-700'}`}
                        onClick={() => {
                          handleColumnAdjustment(count);
                          setShowAdvancedMenu(false);
                        }}
                      >
                        {count}
                      </button>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    );
  }
  
  // Formatar título para slot de tempo
  const formatTimeSlotLabel = (date) => {
    return format(date, 'HH:mm');
  };
  
  // Seção de próximos agendamentos
  const getUpcomingAppointments = () => {
    const now = new Date();
    
    // Filtrar agendamentos para hoje que ainda não terminaram
    const upcomingAppointments = events
      .filter(event => {
        // Verificar se o evento possui todas as propriedades necessárias
        if (!event || !event.start || !event.end || 
            !(event.start instanceof Date) || !(event.end instanceof Date)) {
          return false;
        }
        
        // Comparar apenas a data (sem a hora)
        const eventDay = event.start.getDate();
        const eventMonth = event.start.getMonth();
        const eventYear = event.start.getFullYear();
        
        const nowDay = now.getDate();
        const nowMonth = now.getMonth();
        const nowYear = now.getFullYear();
        
        // Verificar se é o mesmo dia (comparando dia, mês e ano)
        const isSameDay = 
          eventDay === nowDay && 
          eventMonth === nowMonth && 
          eventYear === nowYear;
        
        // Incluir eventos que são para hoje e ainda não terminaram
        return isSameDay && event.end >= now;
      })
      // Ordenar por hora de início
      .sort((a, b) => a.start - b.start)
      // Limitar a 5 agendamentos
      .slice(0, 5);
    
    return upcomingAppointments;
  };
  
  // Componente para o wrapper de slot de tempo
  const TimeSlotWrapper = ({ children }) => {
    // Simplificando o componente - retornando apenas os children sem o indicador de tempo
    return children;
  };
  
  // Função para formatar a distância de tempo
  const formatTimeDistance = (date) => {
    if (!date || !(date instanceof Date)) {
      return "Horário indisponível";
    }
    
    const now = new Date();
    const diffMs = date - now;
    
    // Se for no passado, mostrar "Em andamento"
    if (diffMs < 0) return "Em andamento";
    
    // Converter para minutos
    const diffMins = Math.floor(diffMs / 60000);
    
    if (diffMins < 60) {
      return `Em ${diffMins} min`;
    }
    
    const hours = Math.floor(diffMins / 60);
    const mins = diffMins % 60;
    
    if (mins === 0) {
      return `Em ${hours}h`;
    }
    
    return `Em ${hours}h ${mins}min`;
  };
  
  // Personalizar os componentes do calendário
  const components = {
    event: EventComponent,
    toolbar: props => (
      <TimelineToolbar
        {...props}
        date={date}
        onNavigate={setDate}
        onView={changeView}
        viewLabels={viewLabels}
        views={CUSTOM_VIEWS}
        view={currentView}
        currentView={currentView}
        onColumnAdjust={handleColumnAdjustment}
        onOpenBlockModal={openBlockingModal}
        isMobile={window.innerWidth < 768}
      />
    ),
    resourceHeader: ResourceHeader,
    timeSlotWrapper: TimeSlotWrapper
  };

  // Manipulador para quando um evento é selecionado no calendário
  const handleSelectEvent = (event) => {
    // Chamamos a função onSelectEvent passada como prop para abrir o AppointmentDetailModal
    if (onSelectEvent && event && event.appointment) {
      onSelectEvent(event);
    }
  };

  return (
    <div className="mt-2">
      {/* Calendário */}
      <div className="calendar-container" style={{ height: '800px' }}>
        <Calendar
          localizer={localizer}
          events={events}
          startAccessor="start"
          endAccessor="end"
          resources={resourcesWithAny.slice(0, visibleColumns)}
          resourceIdAccessor="id"
          resourceTitleAccessor="title"
          views={CUSTOM_VIEWS}
          view={currentView}
          date={date}
          onView={changeView}
          onNavigate={setDate}
          onSelectEvent={handleSelectEvent}
          eventPropGetter={eventStyleGetter}
          components={components}
          defaultView="day"
          formats={{
            timeGutterFormat: formatTimeSlotLabel,
            eventTimeRangeFormat: () => {
              // Retornar string vazia para não mostrar o horário na linha abaixo do título
              // uma vez que já estamos mostrando o horário ao lado do nome do cliente
              return '';
            },
            dayHeaderFormat: (date) => format(date, 'dd/MM/yyyy')
          }}
          messages={{
            today: 'Hoje',
            previous: 'Anterior',
            next: 'Próximo',
            day: 'Dia',
            date: 'Data',
            time: 'Hora',
            event: 'Evento',
            allDay: 'Dia inteiro',
            noEventsInRange: 'Não há agendamentos neste dia',
            showMore: (total) => `+ ${total} mais`,
          }}
          min={new Date(new Date().setHours(7, 0, 0))}
          max={new Date(new Date().setHours(21, 0, 0))}
          step={15} // Intervalo de 15 minutos
          timeslots={4} // 4 slots de 15 minutos = 1 hora
        />
      </div>
      
      {/* Estilos adicionais para o calendário */}
      <style>{`
        /* Estilos para tornar o calendário responsivo */
        .rbc-calendar {
          width: 100%;
          max-width: 100%;
          font-family: 'Inter', sans-serif;
        }
        
        /* Compactar o calendário ainda mais para telas pequenas */
        .rbc-calendar {
          font-size: 0.85rem;
        }
        
        /* Eliminação completa de espaço no cabeçalho do calendário */
        .rbc-time-view {
          display: flex;
          flex-direction: column;
          flex: 1 0 0%;
          width: 100%;
          min-height: 0;
          border: 1px solid #ddd;
        }
        
        /* Melhorar a aparência e a legibilidade dos eventos */
        .rbc-event {
          padding: 1px !important;
          min-height: 20px !important;
          border-radius: 2px !important;
        }
        
        /* Remover o título do evento (já será exibido dentro) */
        .rbc-event-label {
          display: none !important;
        }
        
        /* Ajustar o tamanho dos blocos de eventos para serem mais compactos */
        .rbc-event-content {
          font-size: 11px !important;
          line-height: 1.2 !important;
          padding: 0 !important;
          height: 100% !important;
        }
        
        /* Garantir que o conteúdo do evento ocupe toda a altura */
        .rbc-event-content > div {
          height: 100%;
        }
        
        /* Remover todos os espaçamentos desnecessários */
        .rbc-time-header.rbc-overflowing {
          border-right: 0;
        }
        
        /* Remover linha em branco abaixo dos cabeçalhos de profissionais */
        .rbc-row.rbc-row-resource {
          margin-bottom: -1px;
          height: auto !important;
          min-height: 0 !important;
          padding: 0 !important;
        }
        
        /* Eliminar a altura mínima do header para evitar espaço em branco */
        .rbc-time-header {
          min-height: 0;
          height: auto !important;
          margin-bottom: 0;
          padding-bottom: 0;
        }
        
        /* Ajustar espaço entre cabeçalho e grade */
        .rbc-time-content {
          border-top: 0;
          margin-top: -1px;
        }
        
        /* Eliminar espaço abaixo dos nomes dos profissionais */
        .rbc-time-header-content {
          height: auto !important;
          padding-bottom: 0 !important;
          min-height: 0 !important;
        }
        
        /* Ajustar os cabeçalhos de profissionais para ocupar apenas o espaço necessário */
        .rbc-time-header-content .rbc-header {
          height: auto !important;
          padding-bottom: 4px !important;
          display: flex;
          flex-direction: column;
          justify-content: flex-start;
        }
        
        /* Ajustar os cabeçalhos de recursos para evitar espaço inutilizado */
        .rbc-time-header-gutter {
          height: auto !important;
        }
        
        /* Eliminar completamente o espaço em branco na linha do recurso */
        .rbc-time-header-content .rbc-row {
          min-height: 0 !important;
          height: auto !important;
        }
        
        /* Remover espaçamento adicional na grade superior */
        .rbc-header + .rbc-header {
          border-left: 1px solid #ddd;
        }
        
        /* Melhorar o alinhamento dos horários na coluna lateral */
        .rbc-time-gutter .rbc-timeslot-group {
          text-align: right;
          padding-right: 5px;
        }
        
        .rbc-label {
          padding: 0 3px;
          font-size: 0.75rem;
          display: flex;
          align-items: center;
          justify-content: flex-end;
          height: 100%;
        }
        
        /* Reduzir o padding geral da página para dar mais espaço ao calendário */
        .container, .container-fluid {
          padding-left: 5px !important;
          padding-right: 5px !important;
        }
        
        /* Minimizar largura do gutter de tempo */
        .rbc-time-gutter {
          width: 45px !important;
        }
        
        /* Reduzir padding das células */
        .rbc-day-slot .rbc-time-slot {
          border-top: 1px solid #f0f0f0;
          padding: 0;
        }
        
        /* Reduzir o espaço entre colunas */
        .rbc-time-view .rbc-time-header-content {
          margin-left: 0;
          border-left: none;
        }
        
        /* Otimizar espaço do cabeçalho de recursos */
        .resource-header {
          display: flex;
          align-items: center;
          padding: 2px;
          height: 100%;
        }
        
        /* Ajustar a barra de ferramentas para ocupar menos espaço horizontal */
        .rbc-toolbar {
          padding: 5px 2px;
          margin-bottom: 5px;
        }
        
        .rbc-toolbar button {
          padding: 5px 8px;
        }
        
        /* Diminuir o tamanho da fonte em dispositivos menores */
        @media (max-width: 768px) {
          .rbc-toolbar {
            flex-wrap: wrap;
            justify-content: center;
          }
          
          .rbc-toolbar-label {
            margin: 4px 0;
            width: 100%;
            text-align: center;
          }
          
          .rbc-header {
            padding: 2px 1px;
            font-size: 11px;
          }
          
          .rbc-event {
            padding: 1px 2px !important;
          }
          
          .rbc-event-content {
            font-size: 11px;
          }
          
          .rbc-time-header-content {
            min-height: 36px;
          }
          
          .rbc-time-view .rbc-header {
            justify-content: center;
          }
          
          /* Reduzir ainda mais o gutter de tempo em telas pequenas */
          .rbc-time-gutter {
            width: 35px !important;
          }
          
          /* Reduzir o espaço dos textos nas células */
          .rbc-header {
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
          }
        }
        
        /* Reduzir a altura mínima dos slots para caber mais conteúdo */
        .rbc-timeslot-group {
          min-height: 40px;
        }
        
        /* Ajustar os eventos para ocupar melhor o espaço */
        .rbc-event {
          border-radius: 3px;
          box-shadow: 0 1px 2px rgba(0,0,0,0.1);
          padding: 2px 3px !important;
          border: none !important;
        }
        
        /* Garantir que os eventos são renderizados corretamente */
        .rbc-events-container {
          margin-right: 0;
        }
        
        .rbc-event-label {
          font-size: 10px;
        }
        
        /* Otimizar os componentes do ResourceHeader */
        .rbc-time-header-content .rbc-header {
          padding: 4px 1px;
        }
        
        /* Ajustes para os elementos de interface personalizados */
        .current-time-indicator {
          left: 0;
          right: 0;
        }
        
        .current-time-label {
          left: 8px;
          font-size: 9px;
          padding: 1px 4px;
        }
        
        /* Manter as outras regras CSS existentes */
        .rbc-today {
          background-color: rgba(66, 153, 225, 0.1);
        }
        
        .current-time-slot {
          position: relative;
          background-color: rgba(96, 165, 250, 0.07);
        }
        
        .current-time-indicator {
          position: absolute;
          left: 0;
          right: 0;
          top: 0;
          height: 100%;
          pointer-events: none;
          z-index: 10;
          display: flex;
          align-items: center;
        }
        
        .current-time-line {
          position: absolute;
          left: 0;
          right: 0;
          height: 2px;
          background-color: #3B82F6;
          z-index: 1;
        }
        
        .current-time-dot {
          position: absolute;
          left: 0;
          width: 8px;
          height: 8px;
          border-radius: 50%;
          background-color: #3B82F6;
          z-index: 2;
          transform: translateX(-50%) translateY(-50%);
          box-shadow: 0 0 0 2px white;
        }
      `}</style>
      
      {/* Seção de próximos agendamentos */}
      <div className="mt-4 bg-white p-3 rounded-lg shadow">
        <h3 className="text-lg font-medium text-gray-900 mb-3">
          Próximos Agendamentos
          {getUpcomingAppointments().length > 0 && (
            <span className="ml-2 text-sm text-blue-600 font-normal">
              ({getUpcomingAppointments().length} para hoje)
            </span>
          )}
        </h3>
        <div className="mt-2">
          {getUpcomingAppointments().length > 0 ? (
            getUpcomingAppointments().map((appointment, index) => (
              <div 
                key={appointment.id || index} 
                className={`p-3 rounded-md mb-2 ${index === 0 ? 'bg-blue-50 border-l-4 border-blue-500' : 'bg-gray-50'} cursor-pointer hover:bg-gray-100 transition-colors`}
                onClick={() => handleSelectEvent(appointment)}
              >
                <div className="flex justify-between items-start">
                  <div>
                    <div className="flex items-center">
                      <p className="font-medium">{appointment.title || 'Sem nome'}</p>
                      <span className="ml-2 text-xs bg-blue-100 text-blue-800 px-2 py-0.5 rounded-full">
                        {formatTimeDistance(appointment.start)}
                      </span>
                    </div>
                    <p className="text-sm text-gray-600 mt-1">
                      {appointment.start && format(appointment.start, 'HH:mm')} - {appointment.end && format(appointment.end, 'HH:mm')} • 
                      <span className="ml-1">
                        {resourcesWithAny.find(r => r.id === appointment.resourceId)?.title || 'Profissional não especificado'}
                      </span>
                    </p>
                    <p className="text-xs text-gray-500 mt-0.5">
                      {Array.isArray(appointment.appointment?.servicos) 
                        ? appointment.appointment.servicos.map((service, idx) => (
                            <span key={idx} className="mr-1">
                              {typeof service === 'object' && service.nome 
                                ? service.nome 
                                : 'Serviço'}
                              {idx < appointment.appointment.servicos.length - 1 ? ',' : ''}
                            </span>
                          ))
                        : 'Serviço não especificado'
                      }
                    </p>
                  </div>
                  <span className={`px-2 py-1 text-xs rounded-full ${
                    appointment.status === 'canceled' ? 'bg-red-100 text-red-800' :
                    appointment.status === 'pending' ? 'bg-amber-100 text-amber-800' :
                    'bg-green-100 text-green-800'
                  }`}>
                    {appointment.status === 'canceled' ? 'Cancelado' :
                     appointment.status === 'pending' ? 'Pendente' :
                     'Confirmado'}
                  </span>
                </div>
              </div>
            ))
          ) : (
            <p className="text-gray-500 italic">
              {events.some(event => 
                event.start && 
                event.start.getDate() === new Date().getDate() && 
                event.start.getMonth() === new Date().getMonth() && 
                event.start.getFullYear() === new Date().getFullYear()
              ) 
                ? 'Todos os agendamentos de hoje já foram concluídos' 
                : 'Não há agendamentos para hoje'}
            </p>
          )}
        </div>
      </div>
      
      {/* Controles de visualização */}
      <div className="absolute bottom-4 right-4 flex flex-col space-y-2">
        {/* Adicione aqui os controles de visualização que você deseja exibir */}
      </div>
      
      {/* Modal de bloqueio de horário */}
      {blockModalOpen && (
        <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
          <div className="bg-white rounded-lg shadow-xl p-6 w-full max-w-md mx-4">
            <h2 className="text-lg font-semibold mb-4">Bloquear Horário</h2>
            
            {/* Formulário de bloqueio */}
            <div className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Data
                </label>
                <DateInput
                  selectedDate={format(blockingData.date, 'yyyy-MM-dd')}
                  onChange={(dateStr) => {
                    const [year, month, day] = dateStr.split('-').map(Number);
                    const newDate = new Date(year, month - 1, day);
                    setBlockingData({...blockingData, date: newDate});
                  }}
                />
              </div>
              
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hora Início
                  </label>
                  <input
                    type="time"
                    value={blockingData.startTime}
                    onChange={(e) => setBlockingData({...blockingData, startTime: e.target.value})}
                    className="w-full p-2 border rounded-md"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Hora Fim
                  </label>
                  <input
                    type="time"
                    value={blockingData.endTime}
                    onChange={(e) => setBlockingData({...blockingData, endTime: e.target.value})}
                    className="w-full p-2 border rounded-md"
                  />
                </div>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Profissional
                </label>
                <select
                  value={blockingData.professionalId}
                  onChange={(e) => setBlockingData({...blockingData, professionalId: e.target.value})}
                  className="w-full p-2 border rounded-md"
                >
                  <option value="all">Todos os Profissionais</option>
                  {professionals.map(prof => (
                    <option key={prof.id} value={prof.id}>
                      {prof.nome}
                    </option>
                  ))}
                </select>
              </div>
              
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Motivo
                </label>
                <input
                  type="text"
                  value={blockingData.reason}
                  onChange={(e) => setBlockingData({...blockingData, reason: e.target.value})}
                  placeholder="Motivo do bloqueio"
                  className="w-full p-2 border rounded-md"
                />
              </div>
            </div>
            
            {/* Ações */}
            <div className="flex justify-end mt-6 space-x-2">
              <button
                onClick={() => setBlockModalOpen(false)}
                className="px-4 py-2 bg-white border border-gray-300 text-gray-700 rounded-md hover:bg-gray-50"
              >
                Cancelar
              </button>
              <button
                onClick={applyTimeBlocking}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Bloquear
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default AppointmentTimeline; 